import {
    ChangeDetectionStrategy,
    Component,
    computed,
    contentChild,
    effect,
    ElementRef,
    input,
    output,
    signal,
    TemplateRef,
    viewChild,
    ViewEncapsulation,
} from "@angular/core";
import { S25Util } from "../../util/s25-util";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25ModalFooterComponent } from "./s25.modal.footer.component";
import { Merge } from "../../pojo/Util";

@TypeManagerDecorator("s25-ng-modal")
@Component({
    selector: "s25-ng-modal",
    template: `
        @if (isOpen()) {
            <div #s25Modal class="s25-modal" (click)="onOutsideClick($event)" (keydown.escape)="close()" tabindex="-1">
                <div class="s25-modal--content {{ size() }}">
                    @if (resizable()) {
                        <div class="resize left" (mousedown)="startResizing('left')"></div>
                    }
                    <div class="s25-modal--header">
                        <div class="title">
                            <ng-container *ngTemplateOutlet="header(); context: templateContext()" />
                            @if (!header() && title()) {
                                <h4 class="title">{{ title() }}</h4>
                            }
                        </div>
                        <button #closeButton class="s25-modal--close" (click)="close()">
                            <s25-ng-icon [type]="'close'"></s25-ng-icon>
                        </button>
                    </div>
                    <div class="s25-modal--body">
                        @if (body()) {
                            <ng-container *ngTemplateOutlet="body(); context: templateContext()" />
                        } @else {
                            <ng-content></ng-content>
                        }
                    </div>
                    @if (footer() || type() !== "custom") {
                        <s25-ng-modal-footer
                            #footerComponent
                            [content]="footer()"
                            [templateContext]="templateContext()"
                            [type]="type()"
                            (save)="onSave()"
                            (cancel)="close()"
                            (affirm)="onAffirm($event)"
                        ></s25-ng-modal-footer>
                    }
                    @if (resizable()) {
                        <div class="resize right" (mousedown)="startResizing('right')"></div>
                    }
                </div>
            </div>
        }
    `,
    styles: `
        .s25-modal {
            position: fixed;
            top: 0;
            left: 0;
            height: 100%;
            width: 100%;
            background-color: rgba(0, 0, 0, 0.5);
            opacity: 0;
            transition: opacity 100ms ease-out;
            z-index: 1059; /* one less than popover for multiselects */
        }

        .s25-modal--content {
            background: white;
            border-radius: 3px;
            box-shadow:
                0 15px 35px 0 rgb(50 50 93 / 10%),
                0 5px 15px 0 rgb(0 0 0 / 7%);
            width: 90vw;
            max-height: 95vh;
            margin: 2.5vh auto;
            display: flex;
            flex-direction: column;
            transition: transform 300ms ease-out;
            transform: translateY(-50px);
            position: relative;
            max-width: var(--resized-width, var(--max-width));
            min-width: 20ch;
        }

        .s25-modal.resizing .s25-modal--content > :not(.resize) {
            pointer-events: none;
        }

        .s25-modal.open .s25-modal--content {
            transform: none;
        }

        .s25-modal--content.xxl {
            --max-width: 1400px;
        }

        .s25-modal--content.xl {
            --max-width: 1200px;
        }

        .s25-modal--content.lg {
            --max-width: 1000px;
        }

        .s25-modal--content.md {
            --max-width: 800px;
        }

        .s25-modal--content.sm {
            --max-width: 600px;
        }

        .s25-modal--content.xs {
            --max-width: 500px;
        }

        .s25-modal--content.xxs {
            --max-width: 400px;
        }

        .s25-modal--header {
            display: flex;
            justify-content: space-between;
            padding: 0.5em 1em;
            /*border-bottom: 1px solid;*/
        }

        .s25-modal--header .title {
            font-weight: bold;
            margin: auto 0;
            flex: 1;
        }

        .s25-modal--close {
            border: 0;
            background: transparent;
            color: #6c6c6c;
            padding: 0;
            cursor: pointer;
            border-radius: 50%;
            height: 2em;
            width: 2em;
            transition: all 0.2s;
        }

        .s25-modal--close:hover {
            background: #fafaf9;
            color: var(--color-primary) !important;
        }

        .s25-modal--close:focus {
            box-shadow: 0 0 0 2px var(--color-primary);
            color: var(--color-primary) !important;
            outline: 0;
        }

        .s25-modal--close:active {
            background: #f0f1f2;
        }

        .s25-modal--body {
            overflow-y: auto;
            padding: 0.5em 1em;
        }

        .pulsing {
            transform: scale(1.02) !important;
        }

        .open {
            opacity: 1;
        }

        ::ng-deep #s25.nm-party--on s25-ng-modal .s25-modal--content {
            background-color: #3c3d46;
        }

        ::ng-deep #s25.nm-party--on s25-ng-modal .s25-modal--close {
            color: #fff;
        }

        ::ng-deep #s25.nm-party--on s25-ng-modal .s25-modal--close:hover {
            background: #4a4851;
            color: #73b0e2 !important;
        }

        ::ng-deep #s25.nm-party--on s25-ng-modal .s25-modal--close:focus {
            box-shadow: 0 0 0 2px #73b0e2;
            color: #73b0e2 !important;
            background: #4a4851;
        }

        .resize {
            --resize-width: 10px;

            position: absolute;
            width: var(--resize-width);
            height: 100%;
            cursor: ew-resize;
            user-select: none;
        }

        .resize.left {
            left: calc(var(--resize-width) / -2);
        }

        .resize.right {
            right: calc(var(--resize-width) / -2);
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.Emulated,
    standalone: false,
})
export class S25ModalComponent<CloseData extends any = any, TemplateContext extends object = any>
    implements S25Modal<unknown>
{
    // Inputs
    titleInput = input<ModalConfig["title"]>(undefined, { alias: "title" });
    typeInput = input<ModalConfig["type"]>("custom", { alias: "type" });
    sizeInput = input<ModalConfig["size"]>("md", { alias: "size" });
    resizableInput = input<ModalConfig["resizable"]>(false, { alias: "resizable" });
    contextInput = input<ModalConfig<CloseData, TemplateContext>["templateData"]>(undefined, {
        alias: "templateContext",
    });
    inputHeader = input<ModalConfig<CloseData, TemplateContext>["headerTemplate"]>(undefined, { alias: "header" });
    inputBody = input<ModalConfig<CloseData, TemplateContext>["bodyTemplate"]>(undefined, { alias: "body" });
    inputFooter = input<ModalConfig<CloseData, TemplateContext>["footerTemplate"]>(undefined, { alias: "footer" });

    // Outputs
    save = output<void>();
    modalClosed = output<void>();

    // Content views
    contentHeader = contentChild<ModalConfig<CloseData, TemplateContext>["headerTemplate"]>("s25ModalHeader");
    contentBody = contentChild<ModalConfig<CloseData, TemplateContext>["bodyTemplate"]>("s25ModalBody");
    contentFooter = contentChild<ModalConfig<CloseData, TemplateContext>["footerTemplate"]>("s25ModalFooter");

    // Child views
    closeButton = viewChild("closeButton", { read: ElementRef });
    modal = viewChild("s25Modal", { read: ElementRef });
    footerComponent = viewChild("footerComponent", { read: S25ModalFooterComponent });

    // Computed properties
    title = computed(() => this.config()?.title || this.titleInput());
    type = computed(() => this.config()?.type || this.typeInput());
    size = computed(() => this.config()?.size || this.sizeInput());
    resizable = computed(() => this.config()?.resizable || this.resizableInput());
    templateContext = computed<Context<CloseData, TemplateContext>>(() => {
        return {
            ...(this.config()?.templateData || this.contextInput() || <TemplateContext>{}),
            modal: this,
        };
    });
    header = computed(() => this.config()?.headerTemplate || this.inputHeader() || this.contentHeader());
    body = computed(() => this.config()?.bodyTemplate || this.inputBody() || this.contentBody());
    footer = computed(() => this.config()?.footerTemplate || this.inputFooter() || this.contentFooter());

    config = signal<ModalConfig<CloseData, TemplateContext>>(undefined);
    isOpen = signal(false);
    returnFocusTo: HTMLElement;
    resolveClosePromise: (data: any) => void;

    constructor() {
        effect(() => this.closeButton()?.nativeElement.focus());
        effect(() => this.modal()?.nativeElement.classList.add("open"));
    }

    async open(config?: ModalConfig<CloseData, TemplateContext>) {
        const closePromise: Promise<CloseData> = new Promise((resolve) => {
            this.resolveClosePromise = resolve;
        });
        this.returnFocusTo = document.activeElement as HTMLElement;
        this.config.set(config);
        this.isOpen.set(true);
        return closePromise;
    }

    async close(data?: any) {
        if (!this.isOpen()) return;
        this.modal().nativeElement.classList.remove("open");
        await S25Util.delay(100);
        if (!this.isOpen()) return;
        this.config.set(undefined);
        this.resolveClosePromise(data);
        this.resolveClosePromise = null;
        this.returnFocusTo.focus();
        this.modalClosed.emit();
        this.isOpen.set(false);
        await S25Util.delay(0); // Give the view time to update
    }

    stopLoading() {
        this.footerComponent()?.stopLoading();
    }

    onSave() {
        this.save.emit();
    }

    onAffirm(yes: boolean) {
        this.close(yes);
    }

    onOutsideClick(event: MouseEvent) {
        const target = event.target as Element;
        if (target !== this.modal().nativeElement) return;
        return this.pulse(target.firstChild as Element);
    }

    async pulse(element: Element) {
        element.classList.add("pulsing");
        await S25Util.delay(200);
        element.classList.remove("pulsing");
    }

    startResizing(side: "left" | "right") {
        this.modal().nativeElement.classList.add("resizing");
        const resizeModal = (event: MouseEvent) => {
            const offset = side === "left" ? event.clientX : document.body.clientWidth - event.clientX;
            const newWidth = Math.max(0, document.body.clientWidth - offset * 2);
            this.modal().nativeElement.style.setProperty("--resized-width", `${newWidth}px`);
        };

        const stopResize = () => {
            this.modal().nativeElement.classList.remove("resizing");
            window.removeEventListener("mousemove", resizeModal);
            window.removeEventListener("mouseup", stopResize);
        };

        window.addEventListener("mousemove", resizeModal);
        window.addEventListener("mouseup", stopResize);
    }
}

export interface S25Modal<T = void> {
    open(): Promise<T>;
    close(): T;
}

export type ModalConfig<CloseData extends any = any, TemplateContext extends object = any> = {
    title?: string;
    type?: S25ModalFooterComponent["type"];
    size?: "xxs" | "xs" | "sm" | "md" | "lg" | "xl" | "xxl";
    resizable?: boolean;
    templateData?: TemplateContext;
    headerTemplate?: TemplateRef<Context<CloseData, TemplateContext>>;
    bodyTemplate?: TemplateRef<Context<CloseData, TemplateContext>>;
    footerTemplate?: TemplateRef<Context<CloseData, TemplateContext>>;
};

type Context<CloseData extends any, TemplateContext extends object> = Merge<
    TemplateContext,
    {
        modal: S25ModalComponent<CloseData, TemplateContext>;
    }
>;
